import { Banner, Button } from '@smartsash/ui';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import styles from './RouteError.module.css';

import { getErrorMessage } from '@/utils/getErrorMessage';

/**
 * Router-wide error fallback (HS-869).
 *
 * Registering it as `defaultErrorComponent` is what puts a `CatchBoundary` around each match —
 * without it a rejected suspense query (a 403 on a list, say) unmounts the whole app instead of
 * failing one screen. `getErrorMessage` prefers the server's own text, falls back to the
 * translated `messageKey` of a `NormalizedApiError`, and finally to `common.error`.
 */
export function RouteError({ error }: ErrorComponentProps) {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <div className={styles.error} role="alert">
      <Banner type="error" title={t('route.error.title')}>
        {getErrorMessage(error, t('common.error'), t)}
      </Banner>
      <Button
        hierarchy="secondary"
        size="sm"
        type="button"
        onClick={() => {
          void router.invalidate();
        }}
      >
        {t('route.error.retry')}
      </Button>
    </div>
  );
}
